import { useCallback, useEffect, useState } from "react"

import { logger } from "@/lib/logger"

const log = logger("PrimaryNavCollapsed")

/**
 * Deliberately NOT scoped to organization or workspace, like the folded
 * sections: hiding the sidebar is a statement about the user's screen, which is
 * the same screen in every organization.
 */
const STORAGE_KEY = "coval:sidebar:primary-collapsed:v1"

function readStored(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true"
  } catch (error: unknown) {
    log.warn("Could not read the sidebar collapsed state", { error })
    return false
  }
}

function writeStored(collapsed: boolean): void {
  try {
    if (collapsed) {
      window.localStorage.setItem(STORAGE_KEY, "true")
    } else {
      window.localStorage.removeItem(STORAGE_KEY)
    }
  } catch (error: unknown) {
    // A sidebar that cannot remember being hidden still hides.
    log.warn("Could not persist the sidebar collapsed state", { error })
  }
}

/**
 * Whether the user has hidden the docked primary navigation, remembered across
 * navigations, reloads, and sessions.
 *
 * Nothing is read during render: the server render is real, so the first paint
 * is always the docked sidebar and a stored collapse lands on mount.
 */
export function usePrimaryNavCollapsed(): {
  isCollapsed: boolean
  toggle: () => void
} {
  const [isCollapsed, setIsCollapsed] = useState(false)

  useEffect(() => {
    if (readStored()) {
      setIsCollapsed(true)
    }
  }, [])

  const toggle = useCallback(() => {
    setIsCollapsed((current) => {
      const next = !current
      writeStored(next)
      return next
    })
  }, [])

  return { isCollapsed, toggle }
}
